import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, RotateCw } from 'lucide-react';
import { api } from '@/api/client';
import type { ProductivityGapsResp } from '@/api/types';
import { cn } from '@/lib/utils';
import DateRangeFilter, { defaultRange } from '@/components/resources/DateRangeFilter';
import DownloadDatasetButton from '@/components/resources/DownloadDatasetButton';
import TablePagination from '@/components/resources/TablePagination';

export default function ProductivityGapsPage() {
  const { name = '' } = useParams<{ name: string }>();
  const [range, setRange] = useState(() => defaultRange(1));
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['productivity-gaps', name, range.start, range.end],
    queryFn: () =>
      api<ProductivityGapsResp>(
        `/v1/productivity-gaps?resource-id=${encodeURIComponent(name)}` +
          `&start=${encodeURIComponent(range.start)}` +
          `&end=${encodeURIComponent(range.end)}`,
      ),
    enabled: name !== '',
    refetchOnWindowFocus: false,
  });

  // Viewing the gaps page counts as "seen"; reset the unseen badge
  // on the Resources list so it reflects reality next time the user
  // returns. Fire-and-forget — failures are silent.
  const ackMutation = useMutation({
    mutationFn: () =>
      api<unknown>(
        `/v1/resources/${encodeURIComponent(name)}/productivity-gap/ack`,
        { method: 'POST' },
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['resources'] }),
  });

  useEffect(() => {
    if (name) ackMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const rows = data?.productivityGaps ?? [];
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = useMemo(
    () => rows.slice(safePage * pageSize, (safePage + 1) * pageSize),
    [rows, safePage, pageSize],
  );

  useEffect(() => {
    setPage(0);
  }, [range.start, range.end, name]);

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-1.5 text-sm text-muted-foreground min-w-0"
          >
            <Link
              to="/resources"
              className="hover:text-foreground transition-colors shrink-0"
            >
              Resources
            </Link>
            <ChevronRight
              className="h-3.5 w-3.5 shrink-0 opacity-60"
              aria-hidden
            />
            <span className="text-foreground truncate">Productivity gaps</span>
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            <DownloadDatasetButton
              resourceName={name}
              start={range.start}
              end={range.end}
              downloadType="productivity"
              disabled={isFetching || name === ''}
            />
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border hover:bg-muted disabled:opacity-50 transition-colors shrink-0"
            >
              <RotateCw
                className={cn('h-4 w-4', isFetching && 'animate-spin')}
              />
              Refresh
            </button>
          </div>
        </div>
        <h1
          className="text-2xl font-semibold tracking-tight text-primary font-mono truncate"
          title={name}
        >
          {name}
        </h1>
      </div>

      <DateRangeFilter
        start={range.start}
        end={range.end}
        onApply={(start, end) => setRange({ start, end })}
        disabled={isFetching}
      />

      {isLoading && (
        <div className="py-12 text-center text-muted-foreground text-sm">
          Loading productivity gaps…
        </div>
      )}

      {error && (
        <div className="py-3 px-4 rounded-md border border-destructive/30 bg-destructive/5 text-destructive text-sm">
          Failed to load productivity gaps:{' '}
          {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {!isLoading && !error && (
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-left">
                <th className="px-3 py-2.5 font-medium">Time</th>
                <th className="px-3 py-2.5 font-medium">Requested</th>
                <th className="px-3 py-2.5 font-medium">Reason</th>
                <th className="px-3 py-2.5 font-medium">User</th>
                <th className="px-3 py-2.5 font-medium">Project</th>
                <th className="px-3 py-2.5 font-medium">Department</th>
                <th className="px-3 py-2.5 font-medium">Cluster</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="py-16 text-center text-muted-foreground text-sm"
                  >
                    No productivity gaps in this range.
                  </td>
                </tr>
              )}
              {pageRows.map((g, i) => (
                <tr
                  key={`${g.rejectionTime}-${g.user}-${g.clusterId}-${i}`}
                  className="border-t"
                >
                  <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">
                    {formatDate(g.rejectionTime)}
                  </td>
                  <td className="px-3 py-1.5 font-mono">{g.value || '—'}</td>
                  <td className="px-3 py-1.5 text-destructive max-w-[28rem]">
                    {g.reason || '—'}
                  </td>
                  <td className="px-3 py-1.5 font-medium">{g.user || '—'}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">
                    {g.project || '—'}
                  </td>
                  <td className="px-3 py-1.5 text-muted-foreground">
                    {g.department || '—'}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground break-all">
                    {g.clusterId || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination
            page={safePage}
            pageSize={pageSize}
            totalRows={rows.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            rowLabel="gap"
          />
        </div>
      )}
    </div>
  );
}

function formatDate(s: string): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
